/**
 * Forgot Password Page
 * Allows users to request a password reset email.
 *
 * Wired to the auth service (mock or http via the service factory). The flow is
 * intentionally email-enumeration-safe: the same success state is shown whether
 * or not the email matched an account. In mock mode (and DEBUG backends) the
 * service returns a `resetToken` so the demo link round-trips end-to-end.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react'
import { getAuthService } from '@/services/auth'

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [resetToken, setResetToken] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const { resetToken: token } = await getAuthService().forgotPassword(email)
      setResetToken(token ?? null)
      // Always show success to prevent email enumeration.
      setIsSubmitted(true)
    } catch {
      // Even on an unexpected error, keep the enumeration-safe success state.
      setIsSubmitted(true)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center px-4 py-8 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
      </div>

      {/* Content Card */}
      <div className="relative max-w-md w-full animate-in fade-in slide-in-from-bottom-2 duration-500">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm shadow-lg ring-1 ring-white/20 mb-4">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-white mb-2">Forgot Password?</h1>
          <p className="text-primary-100">
            {isSubmitted
              ? "We've sent you instructions"
              : "No worries, we'll send you reset instructions"}
          </p>
        </div>

        {/* Form Card */}
        <div className="card card-elevated rounded-2xl p-8 space-y-6">
          {isSubmitted ? (
            /* Success State */
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-text-primary mb-2">Check your email</h2>
                <p className="text-sm text-text-secondary">
                  We sent a password reset link to
                  <br />
                  <span className="font-medium text-text-primary">{email}</span>
                </p>
              </div>
              <div className="pt-4 space-y-3">
                <p className="text-xs text-text-tertiary">
                  Didn't receive the email? Check your spam folder or
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setIsSubmitted(false)
                    setEmail('')
                  }}
                  className="btn btn-outline btn-sm"
                >
                  Try another email
                </button>
              </div>

              {/* Demo Notice — only when the service surfaced a token
                  (mock mode / DEBUG backends). Hidden in production. */}
              {resetToken && (
                <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    <strong>Demo Mode:</strong> No actual email is sent. Use the link below to
                    simulate password reset.
                  </p>
                  <Link
                    to={`/reset-password?token=${encodeURIComponent(resetToken)}`}
                    className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 hover:underline mt-2 inline-block"
                  >
                    Click here to simulate reset link →
                  </Link>
                </div>
              )}
            </div>
          ) : (
            /* Form State */
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error Alert */}
              {error && (
                <div className="rounded-lg p-4 border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
                  </div>
                </div>
              )}

              {/* Email Input */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="input input-bordered w-full"
                  disabled={isLoading}
                  required
                  autoFocus
                />
                <p className="text-xs text-text-tertiary mt-2">
                  Enter the email address associated with your account
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || !email}
                className="btn btn-primary w-full flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <span className="loading loading-spinner loading-sm" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Send Reset Link
                  </>
                )}
              </button>
            </form>
          )}

          {/* Back to Login */}
          <div className="pt-4 border-t border-surface-overlay">
            <Link
              to="/login"
              className="flex items-center justify-center gap-2 text-sm text-text-secondary hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-6 text-center text-sm text-primary-100">
          <p>Demo application • All data is mock</p>
        </div>
      </div>
    </div>
  )
}
